import React, { useState } from "react";
import { Form, Row, Space } from "@masterteam/mt-design-react";
import { useIntl, FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";

import Collapse from "../Collapse";

import api from "../../providers/api";
import { getLookupName, UnitOfMeasureList, addToast } from "../../helpers";

import { ReactComponent as ChangeKpiDetailsIcon } from "../../assets/images/change-kpi-details.svg";

import "./style.scss";

const { Field } = Form;

const ChangeKPIDetails = ({
  kpiDetails,
  isReadOnly,
  lists,
  values,
  setFieldValue,
  isStrategic
}) => {
  const intl = useIntl();
  const isAr = intl.locale === "ar";

  const theme = useSelector(({ theme }) => theme);
  const [isOrderValid, setIsOrderValid] = useState(true);
  const [isCodeValid, setIsCodeValid] = useState(true);

  const checkIsOrderValid = async () => {
    try {
      const res = await api.kpi.checkIsOrderValid(
        values?.Order,
        kpiDetails?.Objective?.Theme?.Strategy?.ID ??
          kpiDetails?.Objective?.Theme?.Perspective?.ID,
        { KPIID: kpiDetails?.ID, Level: values?.Level?.Value ?? null }
      );
      setIsOrderValid(res.Data);
    } catch (err) {
      addToast.error(err.message, {
        toastId: "get-users-error"
      });
    }
  };

  const checkIsCodeValid = async () => {
    try {
      const res = await api.kpi.checkIsCodeValid(values?.Strategy?.ID, {
        Code: values?.Code,
        KPIID: kpiDetails?.ID,
        Level: values?.Level?.Value ?? null
      });
      setIsCodeValid(res.Data);
    } catch (err) {
      addToast.error(err.message, {
        toastId: "get-users-error"
      });
    }
  };

  return (
    <Collapse
      defaultExpanded
      title={intl.formatMessage({
        id: "change-kpi-details"
      })}
      icon={<ChangeKpiDetailsIcon />}
    >
      <Space direction="vertical" size="large">
        <Space className="section" direction="vertical">
          <Space className="title-wrapper" align="center" size="medium">
            <div className="title">
              <FormattedMessage id="change-request.current-values" />
            </div>
            <hr />
          </Space>

          <Space direction="vertical" size="large" className="body">
            <Row size="large">
              <Field
                label={intl.formatMessage({ id: "kpi.kpi-arabic-name" })}
                inputProps={{
                  placeholder: intl.formatMessage({
                    id: "kpi.kpi-arabic-name"
                  }),
                  value: kpiDetails?.ArabicName
                }}
                hideErrorMessage
                disabled
              />

              <Field
                label={intl.formatMessage({ id: "kpi.kpi-english-name" })}
                inputProps={{
                  placeholder: intl.formatMessage({
                    id: "kpi.kpi-english-name"
                  }),
                  value: kpiDetails?.EnglishName
                }}
                hideErrorMessage
                disabled
              />
            </Row>

            <Row size="large">
              <Field
                type="textarea"
                label={intl.formatMessage({ id: "kpi.kpi-arabic-description" })}
                inputProps={{
                  placeholder: intl.formatMessage({
                    id: "kpi.kpi-arabic-description"
                  }),
                  value: kpiDetails?.ArabicDescription
                }}
                hideErrorMessage
                disabled
              />

              <Field
                type="textarea"
                label={intl.formatMessage({
                  id: "kpi.kpi-english-description"
                })}
                inputProps={{
                  placeholder: intl.formatMessage({
                    id: "kpi.kpi-english-description"
                  }),
                  value: kpiDetails?.EnglishDescription
                }}
                hideErrorMessage
                disabled
              />
            </Row>

            <Row size="large">
              <Field
                type="textarea"
                label={intl.formatMessage({ id: "kpi.kpi-arabic-formula" })}
                inputProps={{
                  placeholder: intl.formatMessage({
                    id: "kpi.kpi-arabic-formula"
                  }),
                  value: kpiDetails?.ArabicEquation
                }}
                hideErrorMessage
                disabled
              />

              <Field
                type="textarea"
                label={intl.formatMessage({ id: "kpi.kpi-english-formula" })}
                inputProps={{
                  placeholder: intl.formatMessage({
                    id: "kpi.kpi-english-formula"
                  }),
                  value: kpiDetails?.EnglishEquation
                }}
                hideErrorMessage
                disabled
              />
            </Row>

            <Row size="large">
              <Field
                label={intl.formatMessage({
                  id: "kpi-info.kpi-owner"
                })}
                inputProps={{
                  placeholder: intl.formatMessage({
                    id: "kpi-info.kpi-owner"
                  }),
                  value: kpiDetails?.OwnerModel?.DisplayName
                }}
                disabled
                hideErrorMessage
              />

              <Field
                label={intl.formatMessage({
                  id: "kpi-info.objective-owner"
                })}
                inputProps={{
                  placeholder: intl.formatMessage({
                    id: "kpi-info.objective-owner"
                  }),
                  value: kpiDetails?.ObjectiveOwnerModel?.DisplayName
                }}
                disabled
                hideErrorMessage
              />
            </Row>

            <Row size="large">
              <Field
                label={intl.formatMessage({
                  id: "common.order"
                })}
                inputProps={{
                  placeholder: intl.formatMessage({
                    id: "common.order"
                  }),
                  value: kpiDetails?.Order
                }}
                disabled
                hideErrorMessage
              />

              <Field
                label={intl.formatMessage({
                  id: "common.code"
                })}
                inputProps={{
                  placeholder: intl.formatMessage({
                    id: "common.code"
                  }),
                  value: kpiDetails?.Code
                }}
                disabled
                hideErrorMessage
              />
            </Row>

            <Row size="large">
              <Field
                label={intl.formatMessage({
                  id: "common.sector"
                })}
                inputProps={{
                  placeholder: intl.formatMessage({
                    id: "common.sector"
                  }),
                  value: kpiDetails?.KPISectors?.map((e) =>
                    isAr ? e?.ArabicName : e.EnglishName
                  ).join(", ")
                }}
                disabled
                hideErrorMessage
              />

              <Field
                label={intl.formatMessage({
                  id: "kpi-info.kpi-source"
                })}
                inputProps={{
                  placeholder: intl.formatMessage({
                    id: "kpi-info.kpi-source"
                  }),
                  value: kpiDetails?.Source
                }}
                disabled
                hideErrorMessage
              />
            </Row>

            <Row size="large">
              <Field
                label={intl.formatMessage({ id: "kpi-info.polarity" })}
                inputProps={{
                  getOptionLabel: (el) => (isAr ? el?.Arabic : el?.English),
                  getOptionValue: (el) => el?.Value,
                  menuPlacement: "top",
                  placeholder: intl.formatMessage({
                    id: "kpi-info.polarity"
                  }),
                  value: getLookupName(
                    theme.Polarities.item.Items,
                    kpiDetails?.Polarity,
                    isAr
                  )
                }}
                disabled
                hideErrorMessage
              />
              <Row size="small">
                <Field
                  label={intl.formatMessage({ id: "kpi.unit-of-measure" })}
                  inputProps={{
                    placeholder: intl.formatMessage({
                      id: "kpi.unit-of-measure"
                    }),
                    value: getLookupName(
                      UnitOfMeasureList?.map((e) => ({
                        ...e,
                        Value: e?.value,
                        English: e?.label,
                        Arabic: e?.labelAr
                      })),
                      kpiDetails?.UnitOfMeasure,
                      isAr
                    )
                  }}
                  hideErrorMessage
                  disabled
                />
                {kpiDetails.UnitOfMeasure === "Value" ? (
                  <>
                    <Field
                      label={intl.formatMessage({
                        id: "kpi.english-unit-type"
                      })}
                      inputProps={{
                        placeholder: intl.formatMessage({
                          id: "kpi.english-unit-type"
                        }),

                        value: kpiDetails?.EnglishUnitDetails
                      }}
                      hideErrorMessage
                      disabled
                    />
                    <Field
                      label={intl.formatMessage({ id: "kpi.arabic-unit-type" })}
                      inputProps={{
                        placeholder: intl.formatMessage({
                          id: "kpi.arabic-unit-type"
                        }),
                        value: kpiDetails?.ArabicUnitDetails
                      }}
                      hideErrorMessage
                      disabled
                    />
                  </>
                ) : null}
              </Row>
            </Row>

            <Row size="large">
              {isStrategic ? (
                <Field
                  label={intl.formatMessage({ id: "common.theme" })}
                  inputProps={{
                    placeholder: intl.formatMessage({ id: "common.theme" }),
                    value: isAr
                      ? kpiDetails?.Objective?.Theme?.ArabicName
                      : kpiDetails?.Objective?.Theme?.EnglishName
                  }}
                  disabled
                  hideErrorMessage
                />
              ) : (
                <Field
                  label={intl.formatMessage({ id: "common.pillar" })}
                  inputProps={{
                    placeholder: intl.formatMessage({ id: "common.pillar" }),
                    value: isAr
                      ? kpiDetails?.Objective?.Pillar?.ArabicName
                      : kpiDetails?.Objective?.Pillar?.EnglishName
                  }}
                  disabled
                  hideErrorMessage
                />
              )}

              <Field
                label={intl.formatMessage({ id: "common.level" })}
                inputProps={{
                  placeholder: intl.formatMessage({ id: "common.level" }),
                  value: getLookupName(
                    theme.ObjectiveLevel.item.Items,
                    kpiDetails?.Objective?.Level?.toString() ?? "",
                    isAr
                  )
                }}
                disabled
                hideErrorMessage
              />
            </Row>

            <Row size="large">
              <Field
                label={intl.formatMessage({
                  id: "common.strategic-objective"
                })}
                inputProps={{
                  placeholder: intl.formatMessage({
                    id: "common.strategic-objective"
                  }),
                  value: isAr
                    ? kpiDetails?.Objective?.ArabicName
                    : kpiDetails?.Objective.EnglishName
                }}
                disabled
                hideErrorMessage
              />
              <div style={{ width: "100%" }} />
            </Row>
          </Space>
        </Space>

        <Space className="section" direction="vertical">
          <Space
            className="updated-values-title-wrapper"
            align="center"
            size="medium"
          >
            <div className="title ">
              <FormattedMessage id="change-request.values-after-change" />
            </div>
            <hr />
          </Space>

          <Space direction="vertical" size="large" className="body">
            <Row size="large">
              <Field
                name="ArabicName"
                label={intl.formatMessage({ id: "kpi.kpi-arabic-name" })}
                inputProps={{
                  placeholder: intl.formatMessage({
                    id: "kpi.kpi-arabic-name"
                  })
                }}
                disabled={isReadOnly}
                hideErrorMessage
                required
              />

              <Field
                name="EnglishName"
                label={intl.formatMessage({ id: "kpi.kpi-english-name" })}
                inputProps={{
                  placeholder: intl.formatMessage({
                    id: "kpi.kpi-english-name"
                  })
                }}
                disabled={isReadOnly}
                hideErrorMessage
                required
              />
            </Row>

            <Row size="large">
              <Field
                type="textarea"
                name="ArabicDescription"
                label={intl.formatMessage({ id: "kpi.kpi-arabic-description" })}
                inputProps={{
                  placeholder: intl.formatMessage({
                    id: "kpi.kpi-arabic-description"
                  })
                }}
                disabled={isReadOnly}
                hideErrorMessage
                required
              />

              <Field
                type="textarea"
                name="EnglishDescription"
                label={intl.formatMessage({
                  id: "kpi.kpi-english-description"
                })}
                inputProps={{
                  placeholder: intl.formatMessage({
                    id: "kpi.kpi-english-description"
                  })
                }}
                disabled={isReadOnly}
                hideErrorMessage
                required
              />
            </Row>

            <Row size="large">
              <Field
                type="textarea"
                name="ArabicEquation"
                label={intl.formatMessage({ id: "kpi.kpi-arabic-formula" })}
                inputProps={{
                  placeholder: intl.formatMessage({
                    id: "kpi.kpi-arabic-formula"
                  })
                }}
                disabled={isReadOnly}
                hideErrorMessage
                required
              />

              <Field
                type="textarea"
                name="EnglishEquation"
                label={intl.formatMessage({ id: "kpi.kpi-english-formula" })}
                inputProps={{
                  placeholder: intl.formatMessage({
                    id: "kpi.kpi-english-formula"
                  })
                }}
                disabled={isReadOnly}
                hideErrorMessage
                required
              />
            </Row>

            <Row size="large">
              <Field
                type="select"
                label={intl.formatMessage({ id: "kpi-info.kpi-owner" })}
                name="OwnerModel"
                inputProps={{
                  options: lists?.users ?? [],
                  getOptionLabel: (el) => el?.DisplayName,
                  getOptionValue: (el) => el?.UserName,
                  menuPlacement: "top",
                  placeholder: intl.formatMessage({ id: "kpi-info.kpi-owner" })
                }}
                disabled={isReadOnly}
                hideErrorMessage
                required
              />

              <Field
                type="select"
                name="ObjectiveOwnerModel"
                label={intl.formatMessage({ id: "kpi-info.objective-owner" })}
                inputProps={{
                  options: lists?.users ?? [],
                  getOptionLabel: (el) => el?.DisplayName,
                  getOptionValue: (el) => el?.UserName,
                  menuPlacement: "top",
                  placeholder: intl.formatMessage({
                    id: "kpi-info.objective-owner"
                  })
                }}
                disabled={isReadOnly}
                hideErrorMessage
                required
              />
            </Row>

            <Row size="large">
              <Field
                name="Order"
                type="number"
                label={intl.formatMessage({ id: "common.order" })}
                inputProps={{
                  min: 0,
                  step: "any",
                  onKeyDown: (evt) => {
                    if (evt.key === "e" || evt.key === "-" || evt.key === "+") {
                      evt.preventDefault();
                    }
                  },
                  placeholder: intl.formatMessage({
                    id: "common.order"
                  }),
                  onBlur: checkIsOrderValid
                }}
                disabled
                hideErrorMessage
                required
              />

              <Field
                name="Code"
                label={intl.formatMessage({ id: "common.code" })}
                inputProps={{
                  placeholder: intl.formatMessage({
                    id: "common.code"
                  }),
                  onBlur: checkIsCodeValid
                }}
                disabled
                hideErrorMessage
                required
              />
            </Row>

            <Row size="large">
              <Field
                type="select"
                name="KPISectors"
                label={intl.formatMessage({
                  id: "common.sector"
                })}
                inputProps={{
                  options: lists.sectors || [],
                  getOptionLabel: (el) =>
                    isAr ? el?.ArabicName : el.EnglishName,
                  getOptionValue: (el) => el?.ID,
                  menuPlacement: "top",
                  isMulti: true,
                  placeholder: intl.formatMessage({
                    id: "common.sector"
                  }),
                  onChange: (e) => {
                    setFieldValue("KPISectors", e);
                  }
                }}
                disabled={isReadOnly}
                hideErrorMessage
                required
              />

              <Field
                name="Source"
                label={intl.formatMessage({ id: "kpi-info.kpi-source" })}
                inputProps={{
                  placeholder: intl.formatMessage({
                    id: "kpi-info.kpi-source"
                  })
                }}
                disabled={isReadOnly}
                hideErrorMessage
              />
            </Row>

            <Row size="large">
              <Field
                type="select"
                label={intl.formatMessage({ id: "kpi-info.polarity" })}
                name="Polarity"
                disabled={isReadOnly}
                inputProps={{
                  options: theme.Polarities.item.Items,
                  getOptionLabel: (el) => (isAr ? el?.Arabic : el?.English),
                  getOptionValue: (el) => el?.Value,
                  menuPlacement: "top",
                  placeholder: intl.formatMessage({
                    id: "kpi-info.polarity"
                  })
                }}
                hideErrorMessage
                required
              />

              <Row size="small">
                <Field
                  type="select"
                  label={intl.formatMessage({ id: "kpi.unit-of-measure" })}
                  name="UnitOfMeasure"
                  disabled={isReadOnly}
                  inputProps={{
                    options: UnitOfMeasureList,
                    getOptionLabel: (el) => (isAr ? el.labelAr : el.label),
                    getOptionValue: (el) => el?.value,
                    menuPlacement: "top",
                    placeholder: intl.formatMessage({
                      id: "kpi.unit-of-measure"
                    })
                  }}
                  hideErrorMessage
                  required
                />

                {values.UnitOfMeasure?.value === "Value" ? (
                  <>
                    <Field
                      name="EnglishUnitDetails"
                      disabled={isReadOnly}
                      label={intl.formatMessage({
                        id: "kpi.english-unit-type"
                      })}
                      inputProps={{
                        placeholder: intl.formatMessage({
                          id: "kpi.english-unit-type"
                        })
                      }}
                      hideErrorMessage
                      required
                    />
                    <Field
                      name="ArabicUnitDetails"
                      disabled={isReadOnly}
                      label={intl.formatMessage({ id: "kpi.arabic-unit-type" })}
                      inputProps={{
                        placeholder: intl.formatMessage({
                          id: "kpi.arabic-unit-type"
                        })
                      }}
                      hideErrorMessage
                      required
                    />
                  </>
                ) : null}
              </Row>
            </Row>
            <Row size="large">
              {isStrategic ? (
                <Field
                  type="select"
                  name="Theme"
                  label={intl.formatMessage({ id: "common.theme" })}
                  inputProps={{
                    options: lists.themes || [],
                    getOptionLabel: (el) =>
                      isAr ? el?.ArabicName : el.EnglishName,
                    getOptionValue: (el) => el?.ID,
                    menuPlacement: "top",
                    placeholder: intl.formatMessage({ id: "common.theme" }),
                    onChange: (e) => {
                      setFieldValue("Theme", e);
                      setFieldValue("Objective", null);
                    }
                  }}
                  disabled={isReadOnly}
                  hideErrorMessage
                  required
                />
              ) : (
                <Field
                  type="select"
                  name="Pillar"
                  label={intl.formatMessage({ id: "common.pillar" })}
                  inputProps={{
                    options: lists?.pillars || [],
                    getOptionLabel: (el) =>
                      isAr ? el?.ArabicName : el.EnglishName,
                    getOptionValue: (el) => el?.ID,
                    menuPlacement: "top",
                    placeholder: intl.formatMessage({ id: "common.pillar" }),
                    onChange: (e) => {
                      setFieldValue("Pillar", e);
                      setFieldValue("Objective", null);
                    }
                  }}
                  disabled={isReadOnly}
                  hideErrorMessage
                  required
                />
              )}

              <Field
                type="select"
                name="Level"
                label={intl.formatMessage({ id: "common.level" })}
                inputProps={{
                  options: theme.ObjectiveLevel.item.Items,
                  getOptionLabel: (el) => (isAr ? el?.Arabic : el.English),
                  getOptionValue: (el) => el?.Value,
                  menuPlacement: "top",
                  placeholder: intl.formatMessage({ id: "common.level" }),
                  onChange: (e) => {
                    setFieldValue("Level", e);
                    setFieldValue("Objective", null);
                  }
                }}
                disabled={isReadOnly || !isStrategic}
                hideErrorMessage
                required
              />
            </Row>
            <Row size="large">
              <Field
                type="select"
                name="Objective"
                label={intl.formatMessage({
                  id: "common.strategic-objective"
                })}
                inputProps={{
                  options:
                    (isStrategic
                      ? values?.Theme?.Objectives?.filter(
                          (e) =>
                            e?.Level?.toString() ===
                            values?.Level?.Value?.toString()
                        )
                      : values?.Pillar?.Objectives?.filter(
                          (e) =>
                            e?.Level?.toString() ===
                            values?.Level?.Value?.toString()
                        )) ?? [],
                  getOptionLabel: (el) =>
                    isAr ? el?.ArabicName : el.EnglishName,
                  getOptionValue: (el) => el?.ID,
                  menuPlacement: "top",
                  placeholder: intl.formatMessage({
                    id: "common.strategic-objective"
                  }),
                  onChange: (e) => {
                    setFieldValue("Objective", e);
                  }
                }}
                disabled={isReadOnly}
                hideErrorMessage
                required
              />
              <div style={{ width: "100%" }} />
            </Row>
          </Space>
        </Space>
      </Space>
    </Collapse>
  );
};

export default ChangeKPIDetails;
